'use client';

import { useTranslations } from 'next-intl';
import { HStack } from '@terra/ui-styled-system';
import { SparklesIcon } from '@terra/ui-icons';
import {
  PREVENTION_WAF_VENDORS,
  type PreventionWafSuggestion,
} from '@terra/shared-contracts-integrations';
import { Alert } from '../../../components/alert';
import { Button } from '../../../components/button';
import { BodyDefault, BodyStrong } from '../../../components/typography';

export type PreventionWafSuggestionCardProps = {
  suggestion: PreventionWafSuggestion;
  isReadOnly?: boolean;
  isApplied?: boolean;
  onUse: (suggestion: PreventionWafSuggestion) => void;
  onHide: () => void;
};

const DETECTED_LABEL_KEY = {
  [PREVENTION_WAF_VENDORS.CLOUDFLARE]: 'detectedCloudflare',
  [PREVENTION_WAF_VENDORS.VERCEL]: 'detectedVercel',
  [PREVENTION_WAF_VENDORS.AWS_WAFV2]: 'detectedAwsWaf',
} as const;

export function PreventionWafSuggestionCard({
  suggestion,
  isReadOnly,
  isApplied,
  onUse,
  onHide,
}: PreventionWafSuggestionCardProps) {
  const t = useTranslations('designSystem.applicationSettings.prevention');

  return (
    <Alert.Root bg="bg.neutral.info" borderColor="border.neutral.info">
      <Alert.Icon asChild>
        <SparklesIcon size={16} />
      </Alert.Icon>
      <Alert.Content>
        <Alert.Description>
          <BodyStrong as="span">
            {t(DETECTED_LABEL_KEY[suggestion.vendor])}
          </BodyStrong>{' '}
          <BodyDefault as="span" color="fg.muted">
            {suggestion.evidence}
          </BodyDefault>
        </Alert.Description>
      </Alert.Content>
      {!isApplied ? (
        <HStack gap={1.5} flexShrink={0} ml="auto">
          <Button variant="ghost" size="xs" onClick={onHide}>
            {t('hideSuggestion')}
          </Button>
          <Button
            variant="outline"
            size="xs"
            onClick={() => onUse(suggestion)}
            disabled={isReadOnly}
          >
            {t('useSuggestion')}
          </Button>
        </HStack>
      ) : null}
    </Alert.Root>
  );
}
